import React, { useMemo, useState } from "react";
import ReferencesAndChainOfThought from "./ReferencesAndChainOfThought";

/**
 * TrialsOneBubbleWithEvidence
 * This is the main component that displays text chunks with evidence references.
 * Clicking an evidence reference opens a drawer from the right side of the screen.
 */
export default function TrialsOneBubbleWithEvidence({ response, handleEvidenceClick }) {
    // Normalize the raw API response into a structured format.
    const data = useMemo(() => normalizeResponse(response), [response]);

    // Create a map of all unique evidence items and assign them a global index.
    // This runs only when the data changes.
    const { evidenceMap, evidenceIndexMap } = useMemo(() => {
        const map = new Map();
        const indexMap = new Map();
        let globalIndex = 1;

        if (data && data.chunks) {
            data.chunks.forEach(chunk => {
                if (chunk.evidence) {
                    chunk.evidence.forEach(ev => {
                        // Ensure evidence is valid and not already processed.
                        if (ev && ev.key && !map.has(ev.key)) {
                            map.set(ev.key, ev);
                            indexMap.set(ev.key, globalIndex++);
                        }
                    });
                }
            });
        }
        return { evidenceMap: map, evidenceIndexMap: indexMap };
    }, [data]);

    // Don't render anything if there's no data.
    if (!data || data.chunks.length === 0) {
        return null;
    }

    const handleEvidenceClickNew = (evidenceKey, currentEvidence) => {
        console.log("handleEvidenceClickNew called with:", evidenceKey, currentEvidence);
        handleEvidenceClick(evidenceKey, currentEvidence);
    }

    return (
        <>
            {/* Main content area where the answer bubble is displayed */}
            <div className="flex flex-col h-full font-sans bg-white">
                <div className="flex-1 overflow-y-auto p-4 md:p-6">
                    <div className="max-w-3xl mx-auto rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                        <div className="space-y-4">
                            {data.chunks.map((chunk, i) => (
                                <TextWithEvidenceRefs
                                    key={i}
                                    text={chunk.text}
                                    evidence={chunk.evidence}
                                    onEvidenceClick={(displayIndex, ev) => {
                                        handleEvidenceClickNew(displayIndex, ev);
                                    }}
                                    evidenceIndexMap={evidenceIndexMap}
                                />
                            ))}
                        </div>
                        <ReferencesAndChainOfThought
                            references={response.references}
                            count={response.count}
                            chain_of_thought={response.chain_of_thought}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}

/**
 * EvidenceDrawer
 * A styled drawer component matching the Chat.js drawer design
 */
export function EvidenceDrawer({ isOpen, onClose, evidence, evidenceIndex }) {
    const [expandedSections, setExpandedSections] = useState({});

    // Toggle section expansion
    const toggleSection = (section) => {
        setExpandedSections((prev) => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    // We only render the drawer's content if there's evidence and it's open
    if (!evidence || !isOpen) {
        return null;
    }

    // Extract all evidence fields into sections, filtering out null and "None" values
    const entries = jsonEntriesFlat(evidence.value).filter(
        ([, value]) => value != null && value !== 'None'
    );

    // Group entries by their top-level key for better organization
    const groupedSections = {};
    entries.forEach(([key, value]) => {
        const topLevelKey = key.split('.')[0];
        if (!groupedSections[topLevelKey]) {
            groupedSections[topLevelKey] = [];
        }
        // Attach the evidence index if available
        let displayIndex = null;
        if (evidence && evidence.key && evidence.evidenceIndexMap) {
            displayIndex = evidence.evidenceIndexMap.get(evidence.key);
        }
        groupedSections[topLevelKey].push([{ key, displayIndex }, value]);
    });

    const drawerStyles = {
        drawer: {
            position: 'fixed',
            top: 0,
            right: 0,
            width: 450,
            height: '100vh',
            background: '#fff',
            boxShadow: '-2px 0 16px rgba(0,0,0,0.18)',
            zIndex: 1000,
            padding: 32,
            display: 'flex',
            flexDirection: 'column',
            borderLeft: '2px solid #e3eaf2',
            transition: 'transform 0.3s ease-out',
            transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
            overflowY: 'auto',
        },
        backdrop: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.2)',
            zIndex: 999,
            opacity: isOpen ? 1 : 0,
            transition: 'opacity 0.3s ease-out',
            pointerEvents: isOpen ? 'auto' : 'none',
        },
        closeButton: {
            alignSelf: 'flex-end',
            marginBottom: 16,
            background: '#e3eaf2',
            border: 'none',
            borderRadius: 4,
            padding: '6px 14px',
            cursor: 'pointer',
            color: '#1a3556',
            fontWeight: 600,
            fontSize: 14,
            transition: 'background 0.2s',
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 20,
        },
        badge: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 28,
            height: 28,
            padding: '0 8px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderRadius: 14,
            fontSize: 14,
            fontWeight: 700,
        },
        title: {
            fontWeight: 600,
            fontSize: 20,
            color: '#1a3556',
        },
        sectionContainer: {
            marginBottom: 16,
            border: '1px solid #e3eaf2',
            borderRadius: 8,
            background: '#fafbfc',
        },
        sectionHeader: {
            fontWeight: 500,
            color: '#1a3556',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            userSelect: 'none',
            padding: '12px 16px',
            background: '#fff',
            borderBottom: expandedSections ? '1px solid #e3eaf2' : 'none',
            transition: 'background 0.2s',
        },
        sectionContent: {
            fontSize: 14,
            color: '#4a5568',
            padding: '12px 16px',
            background: '#fafbfc',
        },
        chevron: {
            marginLeft: 8,
            color: '#888',
            fontSize: 13,
            transition: 'transform 0.2s',
        },
        fieldItem: {
            marginBottom: 12,
            paddingBottom: 12,
            borderBottom: '1px solid #e8ecf0',
        },
        fieldKey: {
            fontSize: 12,
            fontWeight: 600,
            color: '#667eea',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: 6,
        },
        fieldValue: { fontSize: 14, color: '#2d3748', lineHeight: 1.6, maxHeight: 240, overflowY: 'auto', wordBreak: 'break-word' },
    };

    return (
        <>
            {/* Backdrop */}
            <div
                style={drawerStyles.backdrop}
                onClick={onClose}
            />

            {/* Drawer */}
            <div style={drawerStyles.drawer}>
                <button
                    onClick={onClose}
                    style={drawerStyles.closeButton}
                    onMouseEnter={(e) => e.target.style.background = '#d0dce8'}
                    onMouseLeave={(e) => e.target.style.background = '#e3eaf2'}
                >
                    Close
                </button>

                <div style={drawerStyles.header}>
                    <span style={drawerStyles.badge}>{evidenceIndex}</span>
                    <div style={drawerStyles.title}>Evidence Details</div>
                </div>

                {entries.length === 0 ? (
                    <div style={{
                        textAlign: 'center',
                        color: '#718096',
                        fontStyle: 'italic',
                        marginTop: 40,
                        fontSize: 14
                    }}>
                        No data available for this evidence.
                    </div>
                ) : (
                    <>
                        {Object.entries(groupedSections).map(([sectionKey, sectionEntries]) => {
                            const isExpanded = expandedSections[sectionKey] !== false; // Default to expanded
                            const formattedSectionKey = sectionKey
                                .replace(/_/g, ' ')
                                .replace(/\b\w/g, l => l.toUpperCase());

                            return (
                                <div key={sectionKey} style={drawerStyles.sectionContainer}>
                                    <div
                                        style={{
                                            ...drawerStyles.sectionHeader,
                                            borderBottom: isExpanded ? '1px solid #e3eaf2' : 'none',
                                        }}
                                        onClick={() => toggleSection(sectionKey)}
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#f7fafc'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                                    >
                                        {formattedSectionKey}
                                        <span style={{
                                            ...drawerStyles.chevron,
                                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                        }}>
                                            ▼
                                        </span>
                                    </div>
                                    {isExpanded && (
                                        <div style={drawerStyles.sectionContent}>
                                            {sectionEntries.map(([keyObj, value], idx) => {
                                                const { key, displayIndex } = keyObj;
                                                let displayKey = key.includes('.')
                                                    ? key.split('.').slice(1).join(' → ')
                                                    : key;
                                                // If displayIndex is available, show it

                                                return (
                                                    <div
                                                        key={key}
                                                        style={{
                                                            ...drawerStyles.fieldItem,
                                                            borderBottom: idx === sectionEntries.length - 1 ? 'none' : drawerStyles.fieldItem.borderBottom,
                                                        }}
                                                    >
                                                        <div style={drawerStyles.fieldKey}>
                                                            {displayKey.replace(/_/g, ' ')}
                                                        </div>
                                                        <div style={drawerStyles.fieldValue}>
                                                            {formatDetailedValue(value)}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </>
                )}
            </div>
        </>
    );
}

// --- Helper Components ---

/**
 * normalizeResponse
 * Safely parses the API response into a consistent format.
 */
function normalizeResponse(resp) {
    if (!resp) return { chunks: [] };

    const evidencePack = resp.evidence_pack || {};
    const debug = Array.isArray(resp.debug) ? resp.debug : [];

    if (debug.length > 0) {
        const chunks = debug
            .map(d => ({
                text: (d && d.answer_text) || "",
                evidence: Array.isArray(d?.evidence)
                    ? d.evidence.map(key => ({ key, value: evidencePack[key] }))
                    : []
            }))
            .filter(c => c.text && c.text.trim().length > 0);
        return { chunks };
    }

    const msgs = Array.isArray(resp.messages)
        ? resp.messages
        : (typeof resp.messages === "string" ? resp.messages.split(/\n\n+/) : []);

    const chunks = msgs
        .map(m => ({ text: m, evidence: [] }))
        .filter(c => c.text && c.text.trim().length > 0);

    return { chunks };
}

/**
 * TextWithEvidenceRefs
 * Displays a single text chunk and its associated evidence source buttons.
 */
function TextWithEvidenceRefs({ text, evidence, onEvidenceClick, evidenceIndexMap }) {
    const validEvidence = Array.isArray(evidence) ? evidence.filter(e => e && e.key) : [];
    const hasEvidence = validEvidence.length > 0;
    const [showAll, setShowAll] = useState(false);

    // Split text into a headline and the rest for styling.
    let first = text, rest = "";
    if (typeof text === "string") {
        const [f, ...r] = text.split(/\r?\n/);
        first = f;
        rest = r.length ? r.join("\n") : "";
    }

    const visibleEvidence = showAll ? validEvidence : validEvidence.slice(0, 5);
    const remainingCount = validEvidence.length - 5;

    return (
        <div style={{ paddingBottom: '1rem' }}>
            <div style={{ color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                <strong style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#111827', fontSize: '1rem' }}>{first}</strong>
                {rest && <br />}
                {rest}
            </div>
            {hasEvidence && (
                <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid rgba(229, 231, 235, 0.6)' }}>
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            color: '#1e40af',
                            padding: '0.25rem 0.625rem',
                            borderRadius: '0.5rem',
                            display: 'inline-block',
                        }}>Sources</span>
                        {visibleEvidence.map((ev) => {
                            const displayIndex = evidenceIndexMap.get(ev.key);
                            if (!displayIndex) return null;
                            return (
                                <button
                                    key={ev.key}
                                    onClick={() => onEvidenceClick(displayIndex, ev)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '1.75rem',
                                        height: '1.75rem',
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        borderRadius: '9999px',
                                        border: '1px solid #d1d5db',
                                        transition: 'all 0.2s',
                                        background: '#ffffff',
                                        color: '#2563eb',
                                        cursor: 'pointer'
                                    }}
                                    title={`View evidence #${displayIndex}`}
                                >
                                    {displayIndex}
                                </button>
                            );
                        })}
                        {validEvidence.length > 5 && !showAll && (
                            <button
                                onClick={() => setShowAll(true)}
                                style={{
                                    paddingLeft: '0.75rem',
                                    paddingRight: '0.75rem',
                                    height: '1.75rem',
                                    fontSize: '0.8rem',
                                    fontWeight: 500,
                                    color: '#2563eb',
                                    background: '#eff6ff',
                                    borderRadius: '9999px',
                                    transition: 'background-color 0.2s',
                                    border: 'none',
                                    cursor: 'pointer'
                                }}
                                title={`Show ${remainingCount} more source${remainingCount > 1 ? 's' : ''}`}
                            >
                                +{remainingCount}
                            </button>
                        )}
                        {showAll && validEvidence.length > 5 && (
                            <button
                                onClick={() => setShowAll(false)}
                                style={{
                                    paddingLeft: '0.75rem',
                                    paddingRight: '0.75rem',
                                    height: '1.75rem',
                                    fontSize: '0.8rem',
                                    fontWeight: 500,
                                    color: '#2563eb',
                                    textDecoration: 'underline',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer'
                                }}
                            >
                                Show less
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

/**
 * jsonEntriesFlat
 * Recursively flattens a nested object into an array of [key, value] pairs.
 */
function jsonEntriesFlat(obj, prefix = "") {
    if (obj == null || typeof obj !== "object") return [];
    const out = [];
    for (const [k, v] of Object.entries(obj)) {
        const key = prefix ? `${prefix}.${k}` : k;
        if (v != null && typeof v === "object" && !Array.isArray(v)) {
            out.push(...jsonEntriesFlat(v, key));
        } else {
            out.push([key, v]);
        }
    }
    return out;
}

/**
 * formatDetailedValue
 * Renders a value with appropriate formatting based on its type.
 */
function formatDetailedValue(v) {
    if (v == null) return <span style={{ color: '#a0aec0', fontStyle: 'italic' }}>null</span>;

    if (typeof v === "string") {
        if (v.startsWith('http')) {
            return (
                <a
                    href={v}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                        color: '#667eea',
                        textDecoration: 'underline',
                        wordBreak: 'break-all',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                    }}
                >
                    {v}
                    <svg style={{ width: 12, height: 12, flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                </a>
            );
        }
        if (v.length > 250) {
            return <ExpandableText text={v} />;
        }
        return <span style={{ whiteSpace: 'pre-wrap' }}>{v}</span>;
    }

    if (typeof v === "boolean") {
        return (
            <span style={{
                fontFamily: 'monospace',
                fontWeight: 500,
                color: v ? '#48bb78' : '#f56565',
            }}>
                {String(v)}
            </span>
        );
    }

    if (typeof v === "number") {
        return <span style={{ fontFamily: 'monospace', color: '#667eea' }}>{v}</span>;
    }

    if (Array.isArray(v) || typeof v === 'object') {
        return (
            <pre style={{
                fontFamily: 'monospace',
                fontSize: 12,
                background: '#f7fafc',
                borderRadius: 6,
                padding: 12,
                marginTop: 8,
                border: '1px solid #e2e8f0',
                overflowX: 'auto',
                color: '#2d3748',
            }}>
                {JSON.stringify(v, null, 2)}
            </pre>
        );
    }

    return String(v);
}

/**
 * ExpandableText
 * A component for showing a preview of long text with a "Show More" button.
 */
function ExpandableText({ text }) {
    const [expanded, setExpanded] = useState(false);
    const preview = text.substring(0, 250);

    return (
        <div>
            <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                {expanded ? text : preview + "..."}
            </p>
            <button
                onClick={() => setExpanded(!expanded)}
                style={{
                    marginTop: 8,
                    fontSize: 12,
                    color: '#667eea',
                    fontWeight: 600,
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                }}
            >
                {expanded ? "Show Less" : "Show More"}
            </button>
        </div>
    );
}